'use client'

import {
  createContext,
  type FC,
  type PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
} from 'react'

import { type GkAirProxyServiceWebResponseRecommendationsValue } from '@redteclab/api/clients/crosssellers-proxy-service'
import { type Metadata } from '@redteclab/api/clients/retail-media-xsell-service'

import { useGlobalConfigContext } from '../../../global-config'
import {
  udpTrackingDataLayerPushProductClick,
  udpTrackingImpressionCacheGet,
  udpTrackingImpressionStorageSave,
  udpTrackingPushRecommendationAddToCart,
  useUdpTrackingBuildMetadata,
} from '../../../udp-tracking'
import { urlResolverGetSearchPage } from '../../../url-handling'
import { type RecommendationServiceType } from '../../config/RecommendationServiceType'
import { recommendationAccessorGetWidgetAdServerMetadata } from '../../model/recommendationAccessorGetAreaAdServerMetadata'
import { recommendationAccessorGetWidgetTitle } from '../../model/recommendationAccessorGetAreaTitle'
import { recommendationResponseGetOrCreateRecommendationId } from '../../tracking/recommendationResponseTracking'
import {
  type TrackRecommendationUdpProductImpression,
  useRecommendationUdpTrackingProductImpression,
} from '../../udp-tracking/useRecommendationUdpTrackingProductImpression'

interface RecommendationWidgetContextValue {
  adServerMetadata?: Metadata
  moreHref?: string
  saveUdpProductClickAttribution: (upid: string) => void
  serviceId: RecommendationServiceType
  trackUdpProductAddToCart: (params: { quantity: number; upid: string }) => void
  trackUdpProductImpression: TrackRecommendationUdpProductImpression
  widgetId: string
  widgetTitle?: string
}

interface RecommendationWidgetProviderProps {
  serviceId: RecommendationServiceType
  widgetData?: GkAirProxyServiceWebResponseRecommendationsValue
  widgetId: string
}

const Context = createContext<Partial<RecommendationWidgetContextValue>>({})

export const useRecommendationWidgetContext =
  (): Partial<RecommendationWidgetContextValue> => useContext(Context)

export const RecommendationWidgetProvider: FC<
  PropsWithChildren<RecommendationWidgetProviderProps>
> = ({ children, serviceId, widgetData, widgetId }) => {
  const buildMetadata = useUdpTrackingBuildMetadata()
  const globalConfig = useGlobalConfigContext()
  const recommendationId = widgetData
    ? recommendationResponseGetOrCreateRecommendationId(widgetData)
    : undefined
  const adServerMetadata = recommendationAccessorGetWidgetAdServerMetadata({
    widgetData,
  })
  const trackUdpProductImpression =
    useRecommendationUdpTrackingProductImpression({
      adServerMetadata,
      recommendationId,
      widgetId,
    })
  const widgetTitle = recommendationAccessorGetWidgetTitle({ widgetData })
  const trackUdpProductAddToCart = useCallback(
    ({ quantity, upid }: { quantity: number; upid: string }): void => {
      const impression = udpTrackingImpressionCacheGet(
        upid,
        'recommendation',
        widgetId,
      )

      if (!impression) {
        return
      }

      udpTrackingPushRecommendationAddToCart({
        addToCartSource: 'widget',
        buildMetadata,
        impression,
        product: { ...impression.product, quantity, upid },
      })
    },
    [buildMetadata, widgetId],
  )

  const saveUdpProductClickAttribution = useCallback(
    (upid: string): void => {
      udpTrackingImpressionStorageSave({
        impressionOrigin: 'recommendation',
        upid,
        widgetId,
      })

      udpTrackingDataLayerPushProductClick({
        impressionOrigin: 'recommendation',
        metadata: buildMetadata(),
        upid,
        widgetId,
      })
    },
    [buildMetadata, widgetId],
  )

  const moreHref = useMemo(
    // eslint-disable-next-line react-hooks/preserve-manual-memoization -- don't reference, refactor if touched
    () =>
      widgetData?.content
        ? urlResolverGetSearchPage(globalConfig, {
            queryParams: {
              // eslint-disable-next-line id-length -- search param
              q: widgetData.content.map(({ data }) => data.UID).join(','),
            },
          })
        : undefined,
    [widgetData?.content, globalConfig],
  )

  const value = useMemo(
    () => ({
      adServerMetadata,
      moreHref,
      saveUdpProductClickAttribution,
      serviceId,
      trackUdpProductAddToCart,
      trackUdpProductImpression,
      widgetId,
      widgetTitle,
    }),
    [
      widgetTitle,
      moreHref,
      saveUdpProductClickAttribution,
      trackUdpProductAddToCart,
      widgetId,
      adServerMetadata,
      trackUdpProductImpression,
      serviceId,
    ],
  )

  return <Context.Provider value={value}>{children}</Context.Provider>
}
